import {
  CradleCommands,
  ConfigStatus,
  BellStatusMinified,
  RespResponse,
  RingPatterns,
  ScheduledRings,
  ScheduledRing,
  RingPattern,
  silentDispatchList,
} from '../constants/constants';
import { IMqttServiceOptions, MqttConnectionState, IMqttMessage } from 'ngx-mqtt';
import { Injectable } from '@angular/core';
import { MqttService } from 'ngx-mqtt';
import { catchError, EMPTY, filter, first, of, Subscription, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataService } from './data.service';
import { Utils } from '../constants/utils';
import { ApiService } from './api.service';
import { environment } from '../../environment/environment';
import { NotificationService } from './notification.service';
import { LoginConstants } from '../constants/constants';
import { AuthService } from './auth.service';

const hostname = environment.mqttBroker || window.location.host;
export const MQTT_SERVICE_OPTIONS_CUST: IMqttServiceOptions = {
  hostname,
  port: environment.mqttPort || undefined,
  path: '/mqtt',
  connectOnCreate: false,
  clean: true, // Retain session
  connectTimeout: 4000, // Timeout period
  reconnectPeriod: 2000, // Reconnect period
  // Authentication information
  // clientId: 'mqttx_597046f4',
  username: 'mejo',
  password: 'jose',
  protocol: environment.mqttProtocol,
} as IMqttServiceOptions;

@Injectable({
  providedIn: 'root',
})
export class CradleService {
  subscriptions: Subscription[] = [];
  retryStatusCheckInterval: any;

  constructor(
    private _mqttService: MqttService,
    private toastr: NotificationService,
    private dataService: DataService,
    private apiService: ApiService,
    private authService: AuthService
  ) {
    // Subscribe to churchId changes
    this.authService.churchIdChange$.subscribe(churchId => {
      if (churchId) {
        this.createConnection();
      } else {
        // Disconnect and clean up when churchId is cleared
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
        this._mqttService?.disconnect();
      }
    });

    // Initialize if we already have a churchId
    if (this.authService.getChurchId()) {
      this.createConnection();
    }
  }

  private get topicPrefix(): string {
    const churchId = environment.isController? environment.churchId : this.authService.getChurchId();
    if (!churchId) {
      console.warn('No church ID available for MQTT topics');
      return '';
    }
    return churchId + '/';
  }

  // Create a connection
  createConnection() {
    // Connection string, which allows the protocol to specify the connection method to be used
    // ws Unencrypted WebSocket connection
    // wss Encrypted WebSocket connection
    // mqtt Unencrypted TCP connection
    // mqtts Encrypted TCP connection
    this._mqttService?.onConnect.subscribe(() => {
      // console.log('Connection succeeded!');
      this.subscribeToTopics();
    });
    this._mqttService?.onError.subscribe((error: any) => {
      // console.log('Connection failed', error);
    });
    this._mqttService?.onMessage.subscribe((packet: any) => {
      // console.log(
      //   `Received message ${packet.payload.toString()} from topic ${
      //     packet.topic
      //   }`
      // );
    });
    this._mqttService.onEnd.subscribe((end) => {
      console.log('MQTT connection ended', end);
    });
    this._mqttService.onReconnect.subscribe((reconnect) =>
      console.log('MQTT connection reconnected', reconnect)
    );
    this._mqttService.onClose.subscribe((closed) =>
      console.log('Mqtt connection closed', closed)
    );
    try {
      this._mqttService?.connect(MQTT_SERVICE_OPTIONS_CUST);
    } catch (error) {
      console.log('mqtt.connect error', error);
    }
  }

  subscribeToTopics() {
    // Only proceed if we have a churchId
    console.log("subscribing to topic as mqtt got connected");
    if (!this.authService.getChurchId()) {
      console.warn('Cannot subscribe to topics without church ID');
      return;
    }

    let hasRecievedPatterns = false, hasReceivedStatus = false;
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.retryStatusCheckInterval){
      clearInterval(this.retryStatusCheckInterval);
    }

    const sub1 = this._mqttService
      .observe(this.topicPrefix + 'notifications')
      .subscribe((message: IMqttMessage) => {
        const { msg } = JSON.parse(message.payload.toString());
        // console.log(msg);
        this.toastr.info(msg, 'Notification');
      });
    this.subscriptions.push(sub1);
    const sub2 = this.listenToResponse().subscribe(
      (mqttMsg: IMqttMessage) => {
        const resp: RespResponse = JSON.parse(mqttMsg.payload.toString());
        switch (resp.respFor) {
          case CradleCommands.GET_RING_PATTERNS: {
            hasRecievedPatterns = true;
            const ringPatternsDict: RingPatterns = resp.data;
            this.dataService.ringPatterns.next(ringPatternsDict);
            break;
          }
          case CradleCommands.GET_SCHEDULED_RINGS: {
            const sheduledDict: ScheduledRings = resp.data;
            const scheduledRings: ScheduledRing[] = Utils.dictToListSchedule<ScheduledRing>(sheduledDict);
            this.dataService.scheduledRings.next(scheduledRings);
            // console.log('Received ring schedules as :', scheduledRings);
            break;
          }
        }
      },
      (err) =>
        console.error(
          'error occured while listening to response for ' +
            CradleCommands.GET_RING_PATTERNS,
          err
        )
    );
    this.subscriptions.push(sub2)
    const sub3 = this.listenToStatus().subscribe((message: IMqttMessage) => {
      hasReceivedStatus = true;
      const status = Utils.mapPayloadToStatus(message.payload.toString());
      this.dataService.bellStatus.next(status);
    });
    this.subscriptions.push(sub3)
    if(environment.isController){
      const sub4 = this.listenToConfigStatus().subscribe((message: IMqttMessage) => {
        const configStatus = Utils.mapPayloadToConfigStatus(message.payload.toString());
        this.dataService.configStatus.next(configStatus);
      });
      this.subscriptions.push(sub4);
      setTimeout(()=>{
        this.dispatchCommand(CradleCommands.PUBLISH_CONFIG_STATUS);
      },1000)
    }
    setTimeout(() => {
      this.dispatchCommand(CradleCommands.GET_RING_PATTERNS);
      this.dispatchCommand(CradleCommands.PUBLISH_CURRENT_STATUS);
    }, 0);
    let retryCnt = 0;
    this.retryStatusCheckInterval = setInterval(() => {
      if ((hasRecievedPatterns && hasReceivedStatus) || retryCnt > 10) {
        clearInterval(this.retryStatusCheckInterval)
      } else {
        // console.log("retrying to fetch data");
        retryCnt++;
        hasRecievedPatterns || this.dispatchCommand(CradleCommands.GET_RING_PATTERNS);
        hasReceivedStatus || this.dispatchCommand(CradleCommands.PUBLISH_CURRENT_STATUS);
      }
    }, 2000);
  }

  listenToConfigStatus() {
    if (!this.authService.getChurchId()) return EMPTY;
    return this._mqttService.observe(this.topicPrefix + 'configStatus');
  }

  listenToStatus() {
    if (!this.authService.getChurchId()) return EMPTY;
    return this._mqttService.observe(this.topicPrefix + 'status');
  }

  listenToResponse() {
    if (!this.authService.getChurchId()) return EMPTY;
    return this._mqttService.observe(this.topicPrefix + 'response');
  }

  publish(topic: string, message: string) {
    if (!this.authService.getChurchId()) {
      this.toastr.error('Not connected to any church');
      return EMPTY;
    }

    if(environment.isController) {
      return this._mqttService.state.pipe(
        filter(state => state === MqttConnectionState.CONNECTED),
        first(),
        switchMap(st => {
          this._mqttService.unsafePublish(this.topicPrefix + topic, message, { qos: 1, retain: false });
          return of({});
        })
      );
    } else {
      return this.apiService.publishToBell(topic, message);
    }
  }

  dispatchCommand(
    command: CradleCommands,
    value: any = null,
    interval: any = undefined,
    interval2: any = undefined
  ) {
    try {
      this.publish(
        'request',
        JSON.stringify({ c: command, v: value, i: interval, interval2 })
      ).pipe(catchError(err=>{
        this.toastr.error(
          `Error occured while Requesting bell to ${
            command.toString() + (value != null ? ' as ' + value : '')
          }.`,
          'Error'
        );
        return EMPTY;
      })).subscribe(resp=>{
        if(!silentDispatchList.includes(command)){
          this.toastr.success(
            `Requested bell to ${
              command.toString() + (value != null ? ' as ' + value : '')
            }.`,
            'Request placed'
          );
        }
      });

    } catch (err: any) {
      this.toastr.error(err.message);
      console.error(err);
    }
  }
}
